import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Select } from '@/components/ui'
import { Lock, Plus, X, Send, Check } from 'lucide-react'
import { toast } from 'sonner'

const gateways = [
  'Adyen',
  'Cybersource',
  'Stripe',
  'Braintree',
  'PayPal',
  'Square',
  'Authorize.net',
  'Vantiv/Worldpay',
  'FirstData/Fiserv',
  'Chase Paymentech',
  'Global Payments',
  'Heartland',
  'BlueSnap',
  'Checkout.com',
  'Mollie',
  'Klarna',
  'Affirm',
  'Afterpay',
  'Sezzle',
  'Other',
]

export function RequestEncryptor() {
  const [websites, setWebsites] = useState<string[]>(['', ''])
  const [gateway, setGateway] = useState('')
  const [customGateway, setCustomGateway] = useState('')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const addWebsite = () => {
    setWebsites([...websites, ''])
  }

  const removeWebsite = (index: number) => {
    if (websites.length <= 2) {
      toast.error('Minimum 2 websites required')
      return
    }
    setWebsites(websites.filter((_, i) => i !== index))
  }

  const updateWebsite = (index: number, value: string) => {
    const updated = [...websites]
    updated[index] = value
    setWebsites(updated)
  }

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return false
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`)
      return true
    } catch {
      return false
    }
  }

  const handleSubmit = () => {
    // Validate websites
    const validWebsites = websites.filter(w => w.trim())
    if (validWebsites.length < 2) {
      toast.error('Please provide at least 2 websites')
      return
    }

    const invalidUrls = validWebsites.filter(w => !validateUrl(w))
    if (invalidUrls.length > 0) {
      toast.error('Please enter valid website URLs')
      return
    }

    // Validate gateway
    const selectedGateway = gateway === 'Other' ? customGateway : gateway
    if (!selectedGateway.trim()) {
      toast.error('Please select or enter a gateway')
      return
    }

    // Submit request (placeholder - in real app this would send to backend)
    const request = {
      websites: validWebsites,
      gateway: selectedGateway,
      message: message.trim() || null,
      timestamp: new Date().toISOString(),
    }

    console.log('Encryptor Request:', request)
    setSubmitted(true)
    toast.success('Request submitted successfully')
  }

  const resetForm = () => {
    setWebsites(['', ''])
    setGateway('')
    setCustomGateway('')
    setMessage('')
    setSubmitted(false)
  }

  if (submitted) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Request Encryptor</h1>
              <p className="text-muted-foreground">Submit encryption request for a gateway</p>
            </div>
          </div>
        </div>

        <Card className="glass max-w-lg">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 mx-auto">
                <Check className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold">Request Submitted</h2>
              <p className="text-muted-foreground">
                Your encryption request has been submitted successfully. We will process it and get back to you.
              </p>
              <Button onClick={resetForm} className="mt-4">
                Submit Another Request
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Request Encryptor</h1>
            <p className="text-muted-foreground">Submit encryption request for a gateway</p>
          </div>
        </div>
      </div>

      <Card className="glass max-w-2xl">
        <CardHeader>
          <CardTitle>New Request</CardTitle>
          <CardDescription>Fill out the form to request an encryptor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Websites */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>
                Websites <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground ml-2">(minimum 2 required)</span>
              </Label>
              <Button variant="outline" size="sm" onClick={addWebsite}>
                <Plus className="h-4 w-4 mr-1" />
                Add More
              </Button>
            </div>
            <div className="space-y-2">
              {websites.map((website, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={website}
                    onChange={(e) => updateWebsite(index, e.target.value)}
                    placeholder={`https://example${index + 1}.com`}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => removeWebsite(index)}
                    disabled={websites.length <= 2}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Gateway */}
          <div className="space-y-2">
            <Label>
              Gateway <span className="text-red-500">*</span>
            </Label>
            <Select
              value={gateway}
              onChange={(e) => setGateway(e.target.value)}
            >
              <option value="">Select a gateway...</option>
              {gateways.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>
            {gateway === 'Other' && (
              <Input
                value={customGateway}
                onChange={(e) => setCustomGateway(e.target.value)}
                placeholder="Enter gateway name"
                className="mt-2"
              />
            )}
          </div>

          {/* Additional Message */}
          <div className="space-y-2">
            <Label>
              Additional Message
              <span className="text-xs text-muted-foreground ml-2">(optional)</span>
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any additional details or requirements..."
              className="min-h-[100px]"
            />
          </div>

          {/* Submit */}
          <Button onClick={handleSubmit} className="w-full">
            <Send className="h-4 w-4 mr-2" />
            Submit Request
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
